<template>
    <div v-if="property">
        <h4 class="title is-5">
            Key Dates
        </h4>

        <b-notification
            v-if="hasRenewalDate && $dayjs().isBefore(parsedRenewalExpiryDate)"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Renewal date</strong> is on {{ parsedRenewalExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasVacatingDate && $dayjs().isBefore(parsedVacatingExpiryDate)"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Vacating date</strong> is on {{ parsedVacatingExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasTenancyInspectionDate && $dayjs().isBefore(parsedTenancyInspectionExpiryDate)"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Next property inspection</strong> due on {{ parsedTenancyInspectionExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasTenancyInspectionDate && $dayjs().isAfter(parsedTenancyInspectionExpiryDate)"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Property inspection</strong> was due on {{ parsedTenancyInspectionExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasInspectionDate && $dayjs().isBefore(parsedInspectionDate)"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Next property inspection</strong> due on {{ parsedInspectionDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasInspectionDate && $dayjs().isAfter(parsedInspectionDate)"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Property inspection</strong> was due on {{ parsedInspectionDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasEPCDate"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Energy Performance Certificate</strong> (EPC) expire{{ $dayjs().isAfter(parsedEPCExpiryDate) ? 'd' : 's' }} on {{ parsedEPCExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasPATDate"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Portable Appliance Testing</strong> certificate expire{{ $dayjs().isAfter(parsedPATExpiryDate) ? 'd' : 's' }} on {{ parsedPATExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasGasDate"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Gas Safety</strong> certificate expire{{ $dayjs().isAfter(parsedGasExpiryDate) ? 'd' : 's' }} on {{ parsedGasExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasElectricDate"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Electrical Safety</strong> certificate expire{{ $dayjs().isAfter(parsedElectricExpiryDate) ? 'd' : 's' }} on {{ parsedElectricExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasHMODate"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>HMO license</strong> expire{{ $dayjs().isAfter(parsedHMOExpiryDate) ? 'd' : 's' }} on {{ parsedHMOExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasLegionellaDate"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Legionella</strong> check due on {{ parsedLegionellaDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasLicenceDate"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Licence</strong> date expire{{ $dayjs().isAfter(parsedLicenceDate) ? 'd' : 's' }} on {{ parsedLicenceDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasDetectorDate"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Detector</strong> date expire{{ $dayjs().isAfter(parsedDetectorDate) ? 'd' : 's' }} on {{ parsedDetectorDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasBoilerServiceDate"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Boiler service</strong> is due on {{ parsedBoilerServiceDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasOilServiceDate"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Oil service</strong> is due on {{ parsedOilServiceDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasCappedGasDate"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Capped gas</strong> expire{{ $dayjs().isAfter(parsedCappedGasDate) ? 'd' : 's' }} on {{ parsedCappedGasDate | formatDate }}.
        </b-notification>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
    computed: {
        ...mapState('tenancyProperty', [
            'property',
        ]),
        ...mapGetters('tenancyProperty', [
            /// Tenancy:
            // Inspection
            'hasTenancyInspectionDate',
            'parsedTenancyInspectionExpiryDate',

            // Vacating
            'hasVacatingDate',
            'parsedVacatingExpiryDate',

            // Renewal
            'hasRenewalDate',
            'parsedRenewalExpiryDate',

            /// Property:
            // Inspection
            'hasInspectionDate',
            'parsedInspectionDate',

            // EPC
            'hasEPCDate',
            'parsedEPCExpiryDate',

            // PAT
            'hasPATDate',
            'parsedPATExpiryDate',

            // Gas
            'hasGasDate',
            'parsedGasExpiryDate',

            // Electrical Safety Certificate
            'hasElectricDate',
            'parsedElectricExpiryDate',

            // HMO license
            'hasHMODate',
            'parsedHMOExpiryDate',

            // Legionella
            'hasLegionellaDate',
            'parsedLegionellaDate',

            // Licence
            'hasLicenceDate',
            'parsedLicenceDate',

            // Detector
            'hasDetectorDate',
            'parsedDetectorDate',

            // Boiler service
            'hasBoilerServiceDate',
            'parsedBoilerServiceDate',

            // Oil service
            'hasOilServiceDate',
            'parsedOilServiceDate',

            // Capped gas
            'hasCappedGasDate',
            'parsedCappedGasDate',
        ]),
    },
};
</script>
